import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { API } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Calendar, Trash2, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatDateTimeBR, formatCivilDateBR, todayCivilSP } from "@/lib/dates";
import { DADOS } from "../lib/larguras";

const TIPOS = [
  { value: "audiencia", label: "Audiência" },
  { value: "prazo", label: "Prazo" },
  { value: "reuniao", label: "Reunião" },
  { value: "intimacao", label: "Intimação" },
  { value: "outros", label: "Outros" },
];

const TIPO_COLORS = {
  audiencia: "bg-[#003882] text-white",
  prazo: "bg-amber-500 text-white",
  reuniao: "bg-[#335480] text-white",
  intimacao: "bg-emerald-600 text-white",
  outros: "bg-[#6685AD] text-white",
};

function eventDateForDisplay(event) {
  if (event.source === "task" || event.all_day || /^\d{4}-\d{2}-\d{2}$/.test(event.data_inicio || "")) {
    const [year, month, day] = String(event.data_inicio || "").slice(0, 10).split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(event.data_inicio);
}

export default function AgendaPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showAdd, setShowAdd] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState({
    titulo: "",
    descricao: "",
    data_inicio: "",
    data_fim: "",
    tipo: "audiencia",
    processo_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("calendar"); // "calendar" or "list"
  const [selectedEvent, setSelectedEvent] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agenda`, { withCredentials: true });
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Erro ao carregar agenda");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDayClick = (day) => {
    setSelectedDate(day);
    setForm((f) => ({
      ...f,
      data_inicio: format(day, "yyyy-MM-dd") + "T09:00",
    }));
    setShowAdd(true);
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!form.titulo || !form.data_inicio) return;
    setSaving(true);
    try {
      const payload = {
        titulo: form.titulo,
        descricao: form.descricao || null,
        data_inicio: form.data_inicio,
        data_fim: form.data_fim || null,
        tipo: form.tipo,
        processo_id: form.processo_id || null,
      };
      const res = await axios.post(`${API}/agenda`, payload, { withCredentials: true });
      setEvents((prev) => [...prev, res.data]);
      setShowAdd(false);
      setForm({ titulo: "", descricao: "", data_inicio: "", data_fim: "", tipo: "audiencia", processo_id: "" });
      toast.success("Evento adicionado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar evento");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId, source) => {
    if (source === "task") return;
    try {
      await axios.delete(`${API}/agenda/${eventId}`, { withCredentials: true });
      setEvents((prev) => prev.filter((e) => e.event_id !== eventId));
      toast.success("Evento removido");
    } catch {
      toast.error("Erro ao remover evento");
    }
  };

  const openEvent = (event) => {
    if (event.source === "task" && event.task_id) navigate(`/quadros?card_id=${encodeURIComponent(event.task_id)}`);
    else setSelectedEvent(event);
  };

  // Calendar generation
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart, { locale: ptBR });
  const calEnd = endOfWeek(monthEnd, { locale: ptBR });

  const days = [];
  let day = calStart;
  while (day <= calEnd) {
    days.push(day);
    day = addDays(day, 1);
  }

  const eventDateKey = (event) => {
    if (event.source === "task" || event.all_day || /^\d{4}-\d{2}-\d{2}$/.test(event.data_inicio || "")) return String(event.data_inicio || "").slice(0, 10);
    try { return format(new Date(event.data_inicio), "yyyy-MM-dd"); } catch { return ""; }
  };

  const getEventsForDay = (d) => {
    const dayKey = format(d, "yyyy-MM-dd");
    return events.filter((event) => eventDateKey(event) === dayKey);
  };

  const upcomingEvents = events
    .filter((event) => event.source === "task" || event.all_day || /^\d{4}-\d{2}-\d{2}$/.test(event.data_inicio || "")
      ? eventDateKey(event) >= todayCivilSP()
      : new Date(event.data_inicio) >= new Date())
    .sort((a, b) => String(a.data_inicio || "").localeCompare(String(b.data_inicio || "")))
    .slice(0, 20);

  return (
    <Layout>
      <div className={DADOS}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-fade-in-1">
          <div>
            <p className="text-[#335480] text-sm uppercase tracking-widest font-medium mb-1">Organização</p>
            <h1 className="text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Agenda
            </h1>
          </div>
          <div className="flex gap-3">
            <div className="flex border border-[#D1DEEE] rounded-lg overflow-hidden bg-white">
              {[["calendar", "Calendário"], ["list", "Lista"]].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  data-testid={`view-${v}`}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    view === v ? "bg-[#003882] text-white" : "text-[#335480] hover:bg-[#F4F7FB]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <Button
              data-testid="export-agenda-pdf-btn"
              onClick={() => {
                const m = currentMonth.getMonth() + 1;
                const a = currentMonth.getFullYear();
                window.open(`${API}/export/agenda/pdf?mes=${m}&ano=${a}`, "_blank");
              }}
              variant="outline"
              className="border-[#D1DEEE] text-[#335480] hover:text-[#003882] hover:border-[#003882] h-10 px-4"
            >
              <Download size={16} />
              PDF
            </Button>
            <Button
              data-testid="add-event-btn"
              onClick={() => { setForm({ titulo: "", descricao: "", data_inicio: "", data_fim: "", tipo: "audiencia", processo_id: "" }); setShowAdd(true); }}
              className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold h-10 px-5"
            >
              <Plus size={18} />
              Novo evento
            </Button>
          </div>
        </div>

        {view === "calendar" ? (
          <div className="animate-fade-in-2">
            {/* Calendar Nav */}
            <div className="bg-white border border-[#D1DEEE] rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#D1DEEE]">
                <button
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  data-testid="prev-month-btn"
                  className="p-2 rounded-lg text-[#335480] hover:text-[#003882] hover:bg-[#e5edf5] transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <h2 className="text-xl font-bold text-[#001533] capitalize" style={{ fontFamily: "Cabinet Grotesk" }}>
                  {format(currentMonth, "MMMM yyyy", { locale: ptBR })}
                </h2>
                <button
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  data-testid="next-month-btn"
                  className="p-2 rounded-lg text-[#335480] hover:text-[#003882] hover:bg-[#e5edf5] transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-[#D1DEEE]">
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
                  <div key={d} className="py-3 text-center text-sm font-semibold text-[#335480] uppercase tracking-wide">
                    {d}
                  </div>
                ))}
              </div>

              {/* Days grid */}
              <div className="grid grid-cols-7">
                {days.map((d, i) => {
                  const dayEvents = getEventsForDay(d);
                  const isCurrentMonth = isSameMonth(d, currentMonth);
                  const isToday = isSameDay(d, new Date());
                  return (
                    <div
                      key={i}
                      onClick={() => handleDayClick(d)}
                      className={`min-h-[90px] p-2 border-b border-r border-[#D1DEEE] cursor-pointer transition-colors ${
                        isCurrentMonth ? "bg-white hover:bg-[#F4F7FB]" : "bg-[#F4F7FB]/50"
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium mb-1 ${
                          isToday
                            ? "bg-[#003882] text-white"
                            : isCurrentMonth
                            ? "text-[#001533]"
                            : "text-[#D1DEEE]"
                        }`}
                      >
                        {format(d, "d")}
                      </span>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map((evt, j) => (
                          <div
                            key={j}
                            onClick={(e) => { e.stopPropagation(); openEvent(evt); }}
                            onDoubleClick={(e) => { e.stopPropagation(); openEvent(evt); }}
                            title={evt.source === "task" ? "Abrir tarefa no Kanban" : "Ver detalhes"}
                            className={`text-xs px-1.5 py-0.5 rounded truncate font-medium cursor-pointer ${TIPO_COLORS[evt.tipo] || TIPO_COLORS.outros}`}
                          >
                            {evt.source === "task" ? "Tarefa · " : ""}{evt.titulo}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-[#335480]">+{dayEvents.length - 3} mais</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* List View */
          <div className="bg-white border border-[#D1DEEE] rounded-lg overflow-hidden animate-fade-in-2">
            {upcomingEvents.length === 0 ? (
              <div className="p-16 text-center">
                <Calendar size={48} className="mx-auto mb-4 text-[#D1DEEE]" />
                <h3 className="text-xl font-semibold text-[#001533] mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>
                  Nenhum evento próximo
                </h3>
                <p className="text-[#335480]">Adicione audiências, prazos e reuniões.</p>
              </div>
            ) : (
              <div className="divide-y divide-[#D1DEEE]">
                {upcomingEvents.map((evt, i) => (
                  <div
                    key={evt.event_id}
                    data-testid={`event-row-${i}`}
                    onClick={() => openEvent(evt)}
                    onDoubleClick={() => openEvent(evt)}
                    title={evt.source === "task" ? "Abrir tarefa no Kanban" : "Ver detalhes"}
                    className="px-6 py-5 flex items-center justify-between hover:bg-[#F4F7FB] transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center w-14 flex-shrink-0">
                        <p className="text-2xl font-bold text-[#003882]" style={{ fontFamily: "Cabinet Grotesk" }}>
                          {(() => { try { return format(eventDateForDisplay(evt), "dd"); } catch { return "—"; } })()}
                        </p>
                        <p className="text-xs text-[#335480] uppercase font-medium">
                          {(() => { try { return format(eventDateForDisplay(evt), "MMM", { locale: ptBR }); } catch { return ""; } })()}
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TIPO_COLORS[evt.tipo] || TIPO_COLORS.outros}`}>
                            {evt.source === "task" ? "Tarefa" : (TIPOS.find((t) => t.value === evt.tipo)?.label || evt.tipo)}
                          </span>
                        </div>
                        <p className="text-[#001533] text-base font-semibold">{evt.titulo}</p>
                        <p className="text-[#335480] text-sm">
                          {(() => { try { return format(eventDateForDisplay(evt), "HH:mm", { locale: ptBR }); } catch { return ""; } })()}
                          {evt.descricao && ` • ${evt.descricao}`}
                        </p>
                      </div>
                    </div>
                    <button
                      data-testid={`delete-event-${i}`}
                      onClick={() => handleDelete(evt.event_id, evt.source)} disabled={evt.source === "task"} hidden={evt.source === "task"}
                      className="p-2 rounded-lg text-[#335480] hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Event Details Dialog (duplo clique) */}
      <Dialog open={!!selectedEvent} onOpenChange={(o) => !o && setSelectedEvent(null)}>
        <DialogContent className="max-w-md">
          {selectedEvent && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TIPO_COLORS[selectedEvent.tipo] || TIPO_COLORS.outros}`}>
                    {selectedEvent.source === "task" ? "Tarefa" : (TIPOS.find((t) => t.value === selectedEvent.tipo)?.label || selectedEvent.tipo)}
                  </span>
                </div>
                <DialogTitle className="text-xl font-bold text-[#001533] text-left" style={{ fontFamily: "Cabinet Grotesk" }}>
                  {selectedEvent.titulo}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <p className="text-[#335480] text-xs font-medium uppercase tracking-wide mb-1">Início</p>
                  <p className="text-[#001533] text-base">{formatDateTimeBR(selectedEvent.data_inicio)}</p>
                </div>
                {selectedEvent.data_fim && (
                  <div>
                    <p className="text-[#335480] text-xs font-medium uppercase tracking-wide mb-1">Término</p>
                    <p className="text-[#001533] text-base">{formatDateTimeBR(selectedEvent.data_fim)}</p>
                  </div>
                )}
                {selectedEvent.descricao && (
                  <div>
                    <p className="text-[#335480] text-xs font-medium uppercase tracking-wide mb-1">Descrição</p>
                    <p className="text-[#001533] text-base whitespace-pre-wrap">{selectedEvent.descricao}</p>
                  </div>
                )}
                {selectedEvent.processo_id && (
                  <div>
                    <p className="text-[#335480] text-xs font-medium uppercase tracking-wide mb-1">Processo</p>
                    <p className="text-[#001533] text-base">{selectedEvent.processo_id}</p>
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setSelectedEvent(null)} className="flex-1 border-[#D1DEEE]">
                    Fechar
                  </Button>
                  {selectedEvent.source === "task" ? (
                    <Button onClick={() => openEvent(selectedEvent)} className="flex-1 bg-[#003882] text-white">Abrir tarefa</Button>
                  ) : <Button
                    onClick={() => { handleDelete(selectedEvent.event_id, selectedEvent.source); setSelectedEvent(null); }}
                    variant="outline"
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 size={16} /> Excluir
                  </Button>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Novo Evento
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEvent} className="space-y-4">
            <div>
              <Label className="text-[#001533] font-medium">Título *</Label>
              <Input
                data-testid="event-titulo-input"
                value={form.titulo}
                onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
                placeholder="Ex: Audiência de instrução"
                required
                className="mt-1.5 h-11 text-base border-[#D1DEEE]"
              />
            </div>
            <div>
              <Label className="text-[#001533] font-medium">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                <SelectTrigger className="mt-1.5 h-11 text-base border-[#D1DEEE]" data-testid="event-tipo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#001533] font-medium">Início *</Label>
                <Input
                  data-testid="event-data-input"
                  type="datetime-local"
                  value={form.data_inicio}
                  onChange={(e) => setForm((f) => ({ ...f, data_inicio: e.target.value }))}
                  required
                  className="mt-1.5 h-11 text-base border-[#D1DEEE]"
                />
              </div>
              <div>
                <Label className="text-[#001533] font-medium">Fim</Label>
                <Input
                  data-testid="event-data-fim-input"
                  type="datetime-local"
                  value={form.data_fim}
                  onChange={(e) => setForm((f) => ({ ...f, data_fim: e.target.value }))}
                  min={form.data_inicio || undefined}
                  className="mt-1.5 h-11 text-base border-[#D1DEEE]"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#001533] font-medium">Descrição (opcional)</Label>
              <Textarea
                data-testid="event-desc-input"
                value={form.descricao}
                onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                placeholder="Detalhes do evento..."
                rows={2}
                className="mt-1.5 text-base border-[#D1DEEE] resize-none"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)} className="flex-1 border-[#D1DEEE]">
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="save-event-btn"
                disabled={saving}
                className="flex-1 bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
              >
                {saving ? "Salvando..." : "Criar evento"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
